import { Formik, FormikProps } from 'formik';
import { Namespace } from 'i18next';
import {
  LoggedOutScreenWrapper,
  LoggedOutScreenTitle,
  LoggedOutScreenSubtitle,
} from 'LoggedOut/LoggedOutScreenWrapper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { Error, TextInput } from '@unmind/design-system';
import LoadingIndicator from 'Shared/LoadingIndicator';
import { SignUpWithSSOButton } from '../SignUpWithSSOButton';
import { useSubdomainInfo } from '../useSubdomainInfo';
import { StyledForm, StyledSubmitButton } from './CommonFormStyledComponents';
import { RegistrationMethod } from './RegistrationFlow/constants';
import { useGetRegistrationMethods } from './RegistrationFlow/hooks/useGetRegistrationMethods';

export interface EmployeeIdFormProps {
  onSubmit(details: { employeeId: string }): void;
  isInitialValid?: boolean;
  initialValues?: { employeeId: string };
  error?: string;
}

export interface EmployeeIdFormValues {
  [key: string]: string;
}

export const EmployeeIdForm = ({
  isInitialValid,
  initialValues = { employeeId: '' },
  onSubmit,
  error,
}: EmployeeIdFormProps) => {
  const { t: translate } =
    useTranslation<Namespace<'logged_out'>>('logged_out');
  const {
    groupFileFields,
    loading,
    error: subdomainError,
  } = useSubdomainInfo({ subdomain: getSubdomainFromUrl() });
  const { registrationMethods } = useGetRegistrationMethods();

  const employeeIdLabel =
    groupFileFields && groupFileFields[0] && groupFileFields[0].fileHeader;

  const defaultEmployeeIdLabel = translate('sign_up.forms.employee_id.heading');

  const errorMessage = error ?? subdomainError?.message;

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <LoggedOutScreenWrapper dataTestId="signup-id-form">
      <LoggedOutScreenTitle data-testid="form-title">
        {translate('sign_up.forms.employee_id.custom_heading')}{' '}
        {employeeIdLabel}
      </LoggedOutScreenTitle>
      <LoggedOutScreenSubtitle data-testid="form-subtitle">
        {translate('sign_up.forms.employee_id.subtitle')}
      </LoggedOutScreenSubtitle>
      <Formik
        initialValues={initialValues}
        isInitialValid={isInitialValid}
        onSubmit={(values: EmployeeIdFormValues) => {
          onSubmit({
            employeeId: values.employeeId,
          });
        }}
        validate={values => {
          const errors: { [key: string]: string } = {};

          if (!values.employeeId) {
            errors.employeeId = translate(
              'sign_up.forms.employee_id.errors.field_empty',
              {
                file_header: employeeIdLabel,
              },
            );
          }

          return errors;
        }}
      >
        {({
          isValid,
          errors,
          setFieldValue,
          values,
          touched,
          setTouched,
        }: FormikProps<EmployeeIdFormValues>) => (
          <StyledForm>
            <TextInput
              id={'employeeId'}
              additionalText={{
                label: employeeIdLabel || defaultEmployeeIdLabel,
              }}
              onChange={e => {
                setTouched({ employeeId: false });
                setFieldValue('employeeId', e.target.value);
              }}
              onBlur={() => {
                setTouched({ employeeId: true });
              }}
              key={'employeeId'}
              name={'employeeId'}
              placeholder={translate(
                'sign_up.forms.employee_id.custom_id_field.placeholder',
              )}
              value={values.employeeId}
              aria-label={'employeeId'}
              errorText={errors.employeeId}
              hasError={Boolean(touched.employeeId && errors.employeeId)}
            />
            {errorMessage && <Error errorText={errorMessage} showIcon />}
            <StyledSubmitButton
              variant="primary"
              data-testid="continue-button"
              type="submit"
              label={translate('sign_up.forms.employee_id.submit_button.label')}
              disabled={Boolean(errors.employeeId) || !isValid}
              loading={loading}
            />
          </StyledForm>
        )}
      </Formik>
      {registrationMethods.includes(RegistrationMethod.SSO) && (
        <SignUpWithSSOButton />
      )}
    </LoggedOutScreenWrapper>
  );
};
